import { ButtonHTMLAttributes, DetailedHTMLProps, Fragment, PropsWithChildren, useContext, useEffect } from "react"
import {
  BaseTable,
  Button,
  Card,
  DateInput,
  FooterMenu,
  HeaderMenu,
  Input,
  Loading,
  NumberInput,
  Select,
  TableHeader,
  Textarea,
  ActionButton
} from "components"
import { PicturesSection } from "pages/Inventori/Register/ItemGroup/components"
import { Item } from "./Item"
import { LocationContext, LocationProductProvider, LocationProvider, useOptionalCountDetailContext } from "../contexts"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import moment from "moment"
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"
import type { SuccessFetch } from "types"
import { convertNumber, exportPdf, useApi, useEmployee, useToggle } from "utils"
import { type FormType, getDifference, useDataCode } from "../utils"
import { PrintCountInventory } from "./PrintCountInventory"
import { ExportExcelCountInventory } from "./ExportExcelCountInventory"
import { ExportPdfCountInventory } from "./ExportPdfCountInventory"
import { PERMISION } from "security"

type FormSectionTyype = {
  redirectToAdjustment?: boolean,
  onSuccess: () => void
}

export function FormSection(params: PropsWithChildren<FormSectionTyype>) {
  const api = useApi()
  const navigate = useNavigate()
  const { id } = useParams()
  const { isActive, toggle } = useToggle(false)

  const { control, formState, getValues, handleSubmit, reset } = useFormContext<FormType>()
  const countdetail = useWatch({ control, name: "countdetail" })
  const transaction_date = useWatch({ control, name: "transaction_date" })

  const getCountDetail = useOptionalCountDetailContext()

  const onSubmit = (value: FormType) => {
    const { count_id, ...rest } = value

    const callApi = () => {
      const filteredDetails = value.countdetail.map((item) => ({ ...item, different: getDifference(item.soh_count, item.real_count) }))

      if (count_id) {
        return api.put("/invcount/update_inv_count", { ...rest, count_id, invcountdetail: filteredDetails })
      }

      return api.post("/invcount/add", { ...rest, countdetail: filteredDetails })
    }

    return new Promise<number | null>((resolve, reject) => {
      toast
        .promise(callApi(), {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.detail?.message ?? err.response.data.message
        })
        .then((res: SuccessFetch<{ payload: { count_id: number } }>) => resolve(res.data.payload.count_id))
        .catch(() => reject(null))
    })
  }
  const count_id = useWatch({ control, name: "count_id" })

  useEffect(() => {
    if (count_id && !id) {
      navigate(`edit/${count_id}`, { replace: true })
    }
    // eslint-disable-next-line
  }, [count_id])

  return (
    <section className="container my-5 flex flex-col gap-5">
      <HeaderMenu title="DATA ENTRY | COUNT INVENTORY">
        <Code />
      </HeaderMenu>

      <LocationProvider>
        <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
          <section className="flex flex-col gap-3">
            <Location />
            <Employee />

            <Input disabled label="LINK ADJUSTMENT" />
          </section>

          <section className="flex flex-col gap-3">
            <Controller
              control={control}
              name="transaction_date"
              render={({ field, fieldState }) => (
                <DateInput
                  label="DATE"
                  error={fieldState.error?.message}
                  selected={moment(field.value).toDate()}
                  onChange={(date) => {
                    const selectedDate: string = moment(date).format("YYYY-MM-DD")
                    field.onChange(selectedDate)
                  }}
                />
              )}
            />

            <Input disabled label="STATUS" value="Count" />

            <Controller
              name="memo"
              control={control}
              render={({ field, fieldState }) => (
                <Textarea
                  {...field}
                  label="MEMO"
                  error={fieldState.error?.message}
                />
              )}
            />
          </section>
        </section>

        <Card>
          <Card.Body className="flex flex-col gap-3">
            <LocationProductProvider>
              <section className="flex gap-3">
                <section className="grow">
                  <Item />
                </section>

                <PrintButton text="PRINT" onClick={() => toggle()} />
                <PrintButton
                  text="EXCEL"
                  onClick={() => ExportExcelCountInventory({ countdetail, transaction_date, getCountDetail })}
                />
                <PrintButton
                  text="PDF"
                  onClick={() => exportPdf({
                    documentComponent: <ExportPdfCountInventory countdetail={countdetail} transaction_date={transaction_date} getCountDetail={getCountDetail} />,
                    fileName: "count_inventory_item"
                  })}
                />
              </section>

              <BaseTable>
                <TableHeader>
                  <tr>
                    <th>NO</th>
                    <th>ITEM</th>
                    <th>DESCRIPTION</th>
                    <th>UNIT</th>
                    <th>PICTURE</th>
                    <th>SOH</th>
                    <th>COUNT</th>
                    <th>DIFFERENT</th>
                    <th>ACTION</th>
                  </tr>
                </TableHeader>

                <Body />
              </BaseTable>

              {isActive ? (<PrintCountInventory toggle={toggle} />) : null}
            </LocationProductProvider>

            <section className="flex justify-end gap-3">
              {params.redirectToAdjustment && (
                <Button
                  permission={PERMISION.INVENTORY.TRANSACTION.CREATE_COUNT_ITEM.CREATE}
                  type="button"
                  color="primary"
                  onClick={handleSubmit((value) => {
                    const hasUnderZero = value.countdetail?.some(
                      (item) => item.real_count < 0
                    )

                    if (hasUnderZero) {
                      toast.error("Count cannot be zero")
                      return
                    }

                    onSubmit(value).then((res) => {

                      if (res) {
                        navigate(
                          `/inventory/transaction/adjustment-inventory/create/${getValues(
                            "count_id"
                          )}`
                        )
                      }
                    })
                  })}
                >
                  ADJUSTMENT
                </Button>
              )}
            </section>
          </Card.Body>
        </Card>
      </LocationProvider>

      <section className="mt-2">
        <FooterMenu
          hasCancelButton
          save={{
            actiontype: "save",
            type: "button",
            color: "primary",
            loading: formState.isSubmitting ? "true" : undefined,
            onClick: handleSubmit((value) => {
              onSubmit(value).then(() => {
                reset()
                params.onSuccess()
              })
            })
          }}
        />
      </section>
    </section>
  )
}

function Body(): JSX.Element {
  // Hooks

  // const { data } = useContext(LocationProductContext)

  const ctx = useOptionalCountDetailContext()
  // const refetchCountDetail = ctx?.refetchCountDetail
  const data = ctx?.data

  // Form
  const { control, setValue } = useFormContext<FormType>()

  const countdetail = useWatch({
    control,
    name: "countdetail"
  })

  const api = useApi()

  const handleDelete = async (count_detail_id: number) => {
    try {
      await toast.promise(
        api.delete("/invcount/delete_item_count", {
          params: { count_detail_id }
        }),
        {
          loading: "Deleting...",
          success: "Item deleted successfully",
          error: (err) =>
            err.response?.data?.message ??
            err.message ??
            "Failed to delete item"
        }
      )

      // Refetch after deletion
      // if(refetchCountDetail)refetchCountDetail();

      setValue(
        "countdetail",
        countdetail.filter((item) => item.count_detail_id !== count_detail_id)
      )

    } catch (error) {
      console.error("Delete failed:", error)
    }
  }

  return (
    <tbody>
      {!countdetail.length ? (
        <tr>
          <td colSpan={8}>
            <Loading errorText="No data available" />
          </td>
        </tr>
      ) : (
        countdetail.map((item, key) => {
          // Vars
          const contextData = data?.countdetail.find((i) => i.product_id === item.product_id)

          return (
            <Controller
              key={key}
              name={`countdetail.${key}`}
              control={control}
              render={({ field }) => (
                <tr>
                  <td className="text-center">{key + 1}</td>
                  <td>{contextData?.product_barcode}</td>
                  <td>{contextData?.product_name}</td>
                  <td>{contextData?.mr_unit_name}</td>

                  <td>
                    {contextData?.pictures && contextData?.pictures?.length > 0 ? (
                      <section className="flex justify-center">
                        <div className="w-[400px] lg:w-[200px]">

                          <PicturesSection
                            className="h-[100px] w-full"
                            items={1}
                            dataPicture={contextData?.pictures}
                          />
                        </div>
                      </section>
                    ) : ''}
                  </td>

                  <td className="text-right">{convertNumber(Number(item.soh_count)).intoCurrency}</td>

                  <td>
                    <Controller
                      name={`countdetail.${key}.real_count`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <NumberInput
                          error={fieldState.error?.message}
                          value={Number(field.value)}
                          onValueChange={(res) =>{
                            field.onChange(Number(res.floatValue) ?? 0)
                          }
                          }
                        />
                      )}
                    />
                  </td>

                  <td className="text-right">
                    {convertNumber(getDifference(item.soh_count, item.real_count)).intoNormalAmount}
                  </td>
                  <td>
                    <section className="join flex justify-center items-center gap-2">
                      <ActionButton.Delete
                        className="join-item"
                        onClick={() => handleDelete(item.count_detail_id!)}
                      />
                    </section>
                  </td>
                </tr>
              )}
            />
          )
        })
      )}
    </tbody>
  )
}

function Code(): JSX.Element {
  // Hooks
  const { fetchCode } = useDataCode()

  // Form
  const { control, formState, setValue } = useFormContext<FormType>()
  const transaction_date = useWatch({
    control,
    name: "transaction_date"
  })
  const trx_code = useWatch({
    control,
    name: "trx_code"
  })

  useEffect(() => {
    if (!formState.defaultValues?.trx_code) {
      fetchCode(transaction_date)
        .then((res) => {
          setValue("trx_code", res)
        })
        .catch((err) => {
          setValue("trx_code", err)
        })
    }

    // eslint-disable-next-line
  }, [transaction_date])

  return <Fragment>{trx_code}</Fragment>
}

function Employee(): JSX.Element {
  // Hooks
  const { dataEmployee, isLoadingEmployee } = useEmployee()

  // Form
  const { control } = useFormContext<FormType>()

  return (
    <Controller
      name="employee_id"
      control={control}
      render={({ field, fieldState }) => (
        <Select
          label="EMPLOYEE"
          placeholder="Select Employee"
          error={fieldState.error?.message}
          isLoading={isLoadingEmployee}
          value={
            dataEmployee.find((item) => item.value === field.value) ?? null
          }
          options={dataEmployee}
          onChange={(res) => field.onChange(res?.value)}
        />
      )}
    />
  )
}

function Location(): JSX.Element {
  // Hooks
  const { data, isLoading } = useContext(LocationContext)

  // Form
  const { control, setValue } = useFormContext<FormType>()

  return (
    <Controller
      name="location_id"
      control={control}
      render={({ field, fieldState }) => (
        <Select
          label="LOCATION"
          placeholder="Select Location"
          error={fieldState.error?.message}
          isLoading={isLoading}
          value={data.find((item) => item.value === field.value) ?? null}
          options={data}
          onChange={(res) => {
            if (res) {
              field.onChange(res.value)
              setValue("countdetail", [])
            }
          }}
        />
      )}
    />
  )
}

interface Type extends DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> { color?: string, text?: string }
function PrintButton(props: Type) {
  return (
    <Button {...props} type="button" color="primary">
      {props?.text}
    </Button>
  )
}